import React, { useRef, useEffect, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Button, message } from "antd";

/**
 * The signature as a PNG, cropped to the strokes themselves.
 *
 * The box is 340px tall and the signature uses a fraction of it, so saving the whole canvas
 * means a Word template drawing it at 10mm high shrinks the ink far more than 10mm — thin
 * strokes then average out into pale grey. Trimming the empty space makes the picture the
 * signature, so the height it is given is the height it gets.
 *
 * Trimming reads pixel data, so a failure falls back to the untrimmed canvas rather than
 * costing someone their signature.
 */
function readSignatureImage(pad) {
    try {
        return cropToInk(pad.getCanvas()).toDataURL("image/png");
    } catch {
        return pad.getCanvas().toDataURL("image/png");
    }
}

/** A quarter of the ink's height is left as space on every side. */
const INK_PADDING = 0.25;

/**
 * The signature cropped to the strokes themselves, with space left around them, so it
 * prints at the size it is given and never sits flush against a table cell's borders.
 */
function cropToInk(source) {
    const ctx = source.getContext("2d");
    if (!ctx) return source;

    const pixels = ctx.getImageData(0, 0, source.width, source.height).data;

    let left = source.width;
    let right = -1;
    let top = source.height;
    let bottom = -1;

    for (let y = 0; y < source.height; y += 1) {
        for (let x = 0; x < source.width; x += 1) {
            // Every fourth byte is the alpha; anything drawn on has some.
            const alpha = pixels[(y * source.width + x) * 4 + 3];
            if (alpha === 0) continue;

            if (x < left) left = x;
            if (x > right) right = x;
            if (y < top) top = y;
            if (y > bottom) bottom = y;
        }
    }

    // Nothing drawn: hand back what we were given.
    if (right < left || bottom < top) return source;

    const inkWidth = right - left + 1;
    const inkHeight = bottom - top + 1;
    const padding = Math.max(4, Math.round(inkHeight * INK_PADDING));

    const cropped = document.createElement("canvas");
    cropped.width = inkWidth + padding * 2;
    cropped.height = inkHeight + padding * 2;

    const target = cropped.getContext("2d");
    if (!target) return source;

    target.drawImage(source, left, top, inkWidth, inkHeight, padding, padding, inkWidth, inkHeight);
    return cropped;
}

export default function SignaturePad({ onSave, onClear, initialImage, disabled = false, onDelete, hasExistingSignature, height = 340, maxWidth = "400px" }) {
    const sigRef = useRef(null);
    const containerRef = useRef(null);
    const [canvasWidth, setCanvasWidth] = useState(400);

    useEffect(() => {
        const resize = () => {
            if (containerRef.current) {
                setCanvasWidth(containerRef.current.offsetWidth);
            }
        };

        resize();
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, []);

    useEffect(() => {
        if (!initialImage || !sigRef.current) return;

        const canvas = sigRef.current.getCanvas();
        const ctx = canvas.getContext("2d");

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = initialImage;

        img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const scale = Math.max(
                canvas.width / img.width,
                canvas.height / img.height
            );

            const newWidth = img.width * scale;
            const newHeight = img.height * scale;

            const x = (canvas.width - newWidth) / 2;
            const y = (canvas.height - newHeight) / 2;

            ctx.drawImage(img, x, y, newWidth, newHeight);
        };

    }, [initialImage, canvasWidth]);



    const clearSignature = () => {
        sigRef.current.clear();
        onClear?.();
    };

    const saveSignature = () => {
        if (sigRef.current.isEmpty()) {
            
            if (initialImage) {
                onSave(initialImage);
                return;
            }

            message.warning("Cannot save an empty signature field.");
            return;
        }

        const base64 = readSignatureImage(sigRef.current);
        onSave(base64);
        sigRef.current.clear();
    };

    return (
        <div
            ref={containerRef}
            style={{
                width: "100%",
                maxWidth,
                marginTop: "20px",
                marginBottom: "20px",
            }}
        >
            <SignatureCanvas
                ref={sigRef}
                penColor="black"
                minWidth={2.5}
                maxWidth={6}
                dotSize={4}
                canvasProps={{
                    width: canvasWidth,
                    height,
                    style: {
                        width: "100%",
                        height: `${height}px`,
                        border: "1px solid #ccc",
                        borderRadius: 8,
                        display: "block",
                        pointerEvents: disabled ? "none" : "auto",

                    },
                }}
            />

            <div style={{ marginTop: 10 }}>
                <Button onClick={clearSignature} disabled={disabled}>Clear</Button>
                <Button onClick={saveSignature} style={{ marginLeft: 10 }} disabled={disabled}>
                    Save
                </Button>
                {/* {hasExistingSignature && (
                    <Button onClick={onDelete} style={{ marginLeft: 10 }} danger>
                        Delete
                    </Button>
                )} */}
            </div>
        </div>
    );
}
